import { Flex } from './layouts';
import WithLabel from './WithLabel';
import DisplayLine from './DisplayLine';
import { findTranslation } from '../../utils/helpers';
import RadioOption from './RadioOption';
import {ChangeEvent, FC, Fragment} from 'react';
import theme from '../../theme';
import Errors from "./Errors";

export interface RadioProps {
  name: string;
  value?: boolean | string | number;
  options: (
    | { label: string | Element; value: RadioProps['value'] }
    | RadioProps['value']
  )[];
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  autoFocus?: boolean;
  label?: string;
  space?: keyof typeof theme.space;
  direction?: 'row' | 'column';
  errors?: string[] | boolean;
}

const RadioButton: FC<RadioProps> = ({
  value,
  onChange,
  name,
  options,
  space = 's',
  autoFocus,
  direction = 'column',
  readOnly,
  label,
  errors,
}) => {
  if (readOnly) {
    return (
      <DisplayLine label={label} value={findTranslation(value, options)} />
    );
  }

  const input = (
    <Flex
      css={(theme) => ({
        '> *': { flexGrow: 1 },
        '> * + *': { marginTop: direction === 'column' ? theme.space.m : null },
      })}
      minWidth="224px"
      flexDirection={direction}
    >
      {options.map((option, i) => {
        const optionValue = typeof option === 'object' ? option?.value : option;
        const optionLabel = typeof option === 'object' ? option?.label : option;

        return (
          <RadioOption
            key={optionValue.toString()}
            autoFocus={autoFocus && (value === optionValue || i === 0)}
            name={name}
            value={optionValue}
            label={optionLabel}
            space={space}
            onChange={onChange}
            checked={value === optionValue}
          />
        );
      })}
    </Flex>
  );

  const content = (
    <Fragment>
      {input}
      {errors && <Errors errors={errors} />}
    </Fragment>
  )

  return label ? <WithLabel label={label} asDiv>{content}</WithLabel> : content;
};

export default RadioButton;
